import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { type SegmentedTab, type SegmentedTabsProps } from "./SegmentedTabs";

const TabButtonSizeMap = {
  lg: "h-[38px]",
  sm: "h-[32px]",
};

const TabContainerSizeMap = {
  lg: "h-[44px]",
  sm: "h-[40px]",
};

const TabButtonOffsetMap = {
  lg: "3px",
  sm: "4px",
};

export function SegmentedTabsDesktop<T extends number | string>({
  tabs,
  activeTab,
  setActiveTab,
  className,
  size = "sm",
  defaultTabColor = "secondary",
  fluidHeight,
  fullWidth,
  noWrapText,
  ariaLabel,
}: SegmentedTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});

  useEffect(() => {
    if (isFalsy(activeTab)) {
      setActiveTab(tabs[0]?.id);
    }
  }, [activeTab, setActiveTab, tabs]);

  const updateIndicatorPosition = () => {
    if (containerRef.current) {
      const activeTabElement = containerRef.current.querySelector(
        `[data-id="${activeTab}"]`,
      );

      if (activeTabElement instanceof HTMLElement) {
        setIndicatorStyle({
          width: activeTabElement.offsetWidth,
          left: activeTabElement.offsetLeft,
          top: TabButtonOffsetMap[size],
        });
      } else if (tabs.length > 0) {
        setActiveTab(tabs[0].id);
      }
    }
  };

  useEffect(() => {
    updateIndicatorPosition();
  }, [tabs, activeTab, setActiveTab, size]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicatorPosition();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeTab, size]);

  const handleTabClick = (tab: SegmentedTab<T>) => {
    if (isTruthy(tab.disabled)) return;
    setActiveTab(tab.id);
  };

  const activateAndFocus = (tab: SegmentedTab<T>) => {
    setActiveTab(tab.id);
    const element = containerRef.current?.querySelector(
      `[data-id="${tab.id}"]`,
    );

    if (element instanceof HTMLElement) element.focus();
  };

  const moveFocus = (fromIndex: number, direction: -1 | 1) => {
    const total = tabs.length;
    let index = fromIndex;
    for (let step = 0; step < total; step++) {
      index = (index + direction + total) % total;

      if (!isTruthy(tabs[index].disabled)) {
        activateAndFocus(tabs[index]);

        return;
      }
    }
  };

  const focusEdge = (edge: "first" | "last") => {
    const ordered = edge === "first" ? tabs : tabs.toReversed();
    const target = ordered.find((tab) => !isTruthy(tab.disabled));

    if (target) activateAndFocus(target);
  };

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    tab: SegmentedTab<T>,
    index: number,
  ) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        handleTabClick(tab);
        break;
      case "ArrowRight":
        event.preventDefault();
        moveFocus(index, 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        moveFocus(index, -1);
        break;
      case "Home":
        event.preventDefault();
        focusEdge("first");
        break;
      case "End":
        event.preventDefault();
        focusEdge("last");
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={cnTwMerge(
        "w-full max-w-3xl",
        { "w-min": !fullWidth },
        className,
      )}
      data-testid="segmented-tabs-desktop"
    >
      <div
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        className={cnTwMerge(
          "relative isolate flex gap-1 md:gap-2 bg-background/component rounded-default px-1",
          TabContainerSizeMap[size],
          { "h-full": fluidHeight },
        )}
        data-testid="segmented-tabs-desktop-container"
        ref={containerRef}
        role="tablist"
      >
        {/* Background Line */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-full h-px bg-border z-0"
        />

        {/* Active Tab Indicator (Sliding Box) */}
        <div
          aria-hidden="true"
          className={cnTwMerge(
            "absolute h-full rounded-default shadow-card bg-background/primary transition-all duration-300 z-10 px-4",
            TabButtonSizeMap[size],
            { "h-[calc(100%-8px)]": fluidHeight },
          )}
          style={indicatorStyle}
        />

        {/* Tabs */}
        {tabs.map((tab, index) => (
          <div
            aria-disabled={tab.disabled}
            aria-selected={activeTab === tab.id}
            className={cnTwMerge(
              "flex items-center gap-2 py-3 cursor-pointer select-none relative z-20 flex-1 justify-center px-4 rounded-default",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-default",
              isTruthy(tab.disabled) && "cursor-not-allowed opacity-50",
            )}
            data-id={tab.id}
            key={tab.id}
            onClick={() => handleTabClick(tab)}
            onKeyDown={(event) => handleTabKeyDown(event, tab, index)}
            role="tab"
            tabIndex={!isTruthy(tab.disabled) && activeTab === tab.id ? 0 : -1}
          >
            {isTruthy(tab.icon) && tab.iconPosition !== "right"
              ? tab.icon
              : null}
            <span
              className={cnTwMerge(
                "body-base-regular transition-colors shrink-0",
                defaultTabColor === "secondary"
                  ? "text-text/secondary"
                  : "text-text/disabled",
                {
                  "text-text/primary": activeTab === tab.id,
                  "text-text/disabled": tab.disabled,
                  "text-nowrap": noWrapText,
                },
              )}
            >
              {tab.label}
            </span>
            {isTruthy(tab.icon) && tab.iconPosition === "right"
              ? tab.icon
              : null}
          </div>
        ))}
      </div>
    </div>
  );
}
